import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

const SpeechRecognitionAPI = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

// Iter 92 · un solo micrófono activo a la vez en todo el formulario — evita
// que dos botones queden "grabando" (pulsando en rojo) simultáneamente.
// Apunta al `myRef` (identidad estable por instancia) del botón activo, no a
// una función `stop` directa — así nunca queda apuntando a un closure viejo
// si el componente se re-renderiza mientras está grabando (ej. el usuario
// escribe en el campo mientras dicta).
let activeRef = null;

/** Iter 92 · dictado por voz (Web Speech API nativa del navegador, sin backend).
 * Requiere internet (el reconocimiento de Chrome no funciona offline) y HTTPS.
 * Si el navegador no lo soporta, no renderiza nada — nunca rompe el formulario. */
export function VoiceDictationButton({ onResult, disabled = false, testId }) {
    const [listening, setListening] = useState(false);
    // Iter 93 · indicador de conexión — el dictado requiere internet (no es
    // offline-first como el resto de la PWA), y en campo la señal es
    // irregular. `navigator.onLine` detecta "sin red" de entrada; una
    // desconexión A MITAD de la grabación la captura `onerror` (e.error
    // === 'network') más abajo.
    const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
    const recognitionRef = useRef(null);
    const myRef = useRef(null); // identidad estable de ESTA instancia entre renders

    useEffect(() => {
        const goOnline = () => setOnline(true);
        const goOffline = () => setOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    const stop = () => {
        recognitionRef.current?.stop();
        recognitionRef.current = null;
        setListening(false);
        if (activeRef === myRef) activeRef = null;
    };
    myRef.current = stop; // siempre el closure más reciente

    if (!SpeechRecognitionAPI) return null;

    const start = () => {
        if (!online) {
            toast.error('Sin conexión a internet — el dictado por voz no funciona sin señal. Escribe la observación manualmente.');
            return;
        }
        if (activeRef && activeRef !== myRef) activeRef.current(); // corta cualquier otro micrófono activo
        const recognition = new SpeechRecognitionAPI();
        recognition.lang = 'es-MX';
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.onresult = (e) => {
            const transcript = Array.from(e.results).map((r) => r[0].transcript).join(' ').trim();
            if (transcript) onResult(transcript);
        };
        recognition.onerror = (e) => {
            if (e.error === 'network') {
                toast.error('Se perdió la conexión a internet durante el dictado — intenta de nuevo con señal.');
            } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
                toast.error('No se pudo usar el dictado por voz — requiere conexión a internet.');
            }
            recognitionRef.current = null;
            if (activeRef === myRef) activeRef = null;
            setListening(false);
        };
        recognition.onend = () => {
            recognitionRef.current = null;
            if (activeRef === myRef) activeRef = null;
            setListening(false);
        };
        recognitionRef.current = recognition;
        activeRef = myRef;
        setListening(true);
        recognition.start();
    };

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => (listening ? stop() : start())}
            title={!online ? 'Sin conexión — el dictado por voz requiere internet' : (listening ? 'Detener dictado' : 'Dictar observación por voz')}
            aria-label={!online ? 'Sin conexión — el dictado por voz requiere internet' : (listening ? 'Detener dictado' : 'Dictar observación por voz')}
            data-testid={testId}
            data-online={online}
            className={`shrink-0 p-2 border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                listening ? 'animate-pulse' : 'hover:bg-black/5 dark:hover:bg-white/10'
            }`}
            style={{
                borderColor: listening ? '#EF4444' : (!online ? '#F59E0B' : 'var(--border)'),
                background: listening ? 'rgba(239,68,68,0.12)' : (!online ? 'rgba(245,158,11,0.1)' : 'transparent'),
                color: listening ? '#EF4444' : (!online ? '#F59E0B' : 'inherit'),
            }}
        >
            {listening ? <MicOff className="w-4 h-4" /> : (!online ? <WifiOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />)}
        </button>
    );
}
